import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReusableTable from "./../components/ReusableTable";
import { getApiUrl } from "./../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ShippingOffer = () => {
  const UserId = "admin123";

  const [rules, setRules] = useState([]);
  const [statesList, setStatesList] = useState([]); // all states for dropdown
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // fetch delivery rules
  const fetchRules = async () => {
    try {
      const res = await fetch(getApiUrl("delivery-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) setRules(data.data);
    } catch (err) {
      toast.error("Server error! Please try again.");
    }
  };

  // fetch states for multi-select
  const fetchStates = async () => {
    try {
      const res = await fetch(getApiUrl("state-ui-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) {
        setStatesList(data.data); // array of {state, districts}
      }
    } catch (err) {
      toast.error("Could not fetch states");
    }
  };

  useEffect(() => {
    fetchRules();
    fetchStates();
  }, []);

  const handleDelete = async (id) => {
    const res = await fetch(getApiUrl("delivery-delete"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ruleId: id }),
    });
    const result = await res.json();
    if (result.success) {
      toast.success("Deleted Successfully!");
      fetchRules();
    }
  };

  const openAddModal = () => {
    setEditId(null);
    formik.resetForm();
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditId(rule._id);
    formik.setFieldValue("ruleName", rule.ruleName);
    formik.setFieldValue(
      "states",
      rule.states // array of selected states
    );
    formik.setFieldValue("baseCharge", rule.baseCharge);
    formik.setFieldValue("piecesPerKg", rule.piecesPerKg);
    formik.setFieldValue("perKgCharge", rule.perKgCharge);
    setShowModal(true);
  };

  const formik = useFormik({
    initialValues: {
      ruleName: "",
      states: [], // multi-select
      baseCharge: "",
      piecesPerKg: 3,
      perKgCharge: "",
    },
    validationSchema: Yup.object({
      ruleName: Yup.string().required("Rule name required"),
      states: Yup.array().min(1, "Select at least one state"),
      baseCharge: Yup.number().required("Base charge required"),
      piecesPerKg: Yup.number().required("Pieces per kg required"),
      perKgCharge: Yup.number().required("Per kg charge required"),
    }),
    onSubmit: async (values) => {
      const body = {
        ruleName: values.ruleName,
        states: values.states,
        baseCharge: Number(values.baseCharge),
        piecesPerKg: Number(values.piecesPerKg),
        perKgCharge: Number(values.perKgCharge),
      };
      if (editId) body.ruleId = editId;

      const url = editId
        ? getApiUrl("delivery-update")
        : getApiUrl("delivery-add");

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = await res.json();

      if (result.success) {
        toast.success(editId ? "Updated Successfully!" : "Added Successfully!");
        setShowModal(false);
        fetchRules();
      } else {
        toast.error(result.message);
      }
    },
  });

  const columns = [
    { name: "Rule Name", selector: (row) => row.ruleName },
    { name: "States", selector: (row) => row.states.join(", ") },
    { name: "Base Charge", selector: (row) => row.baseCharge },
    { name: "Pieces Per Kg", selector: (row) => row.piecesPerKg },
    { name: "Per Kg Charge", selector: (row) => row.perKgCharge },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <button
            className="btn btn-warning btn-sm me-2"
            onClick={() => openEditModal(row)}
          >
            <i className="fa-regular fa-pen-to-square"></i>
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDelete(row._id)}
          >
            <i className="fa-regular fa-trash-can"></i>
          </button>
        </>
      ),
    },
  ];

  return (
    <div className="container mt-4">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="d-flex justify-content-between mb-3">
        <h3 className="fw-bold">Shipping / Delivery Rules</h3>
        <button className="btn btn-primary" onClick={openAddModal}>
          + Add Rule
        </button>
      </div>

      <ReusableTable columns={columns} data={rules} title="Delivery Rules" />

      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content rounded-4">
              <div className="modal-header">
                <h4 className="modal-title fw-bold">
                  {editId ? "Edit Rule" : "Add Rule"}
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>

              <form onSubmit={formik.handleSubmit}>
                <div className="modal-body">
                  {/* Floating label input for Rule Name */}
                  <div className="floating-label-group mb-3">
                    <input
                      type="text"
                      name="ruleName"
                      placeholder="Rule Name"
                      value={formik.values.ruleName}
                      onChange={formik.handleChange}
                      className="form-control"
                    />
                    <label>Rule Name *</label>
                  </div>

                  {/* Multi-select states */}
                  <div className="mb-3">
                    <label>States *</label>
                    <select
                      multiple
                      name="states"
                      className="form-select"
                      value={formik.values.states}
                      onChange={(e) => {
                        const options = Array.from(
                          e.target.selectedOptions,
                          (option) => option.value
                        );
                        formik.setFieldValue("states", options);
                      }}
                    >
                      {statesList.map((s) => (
                        <option key={s._id} value={s.state}>
                          {s.state}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Other floating label inputs */}
                  <div className="floating-label-group mb-3">
                    <input
                      type="number"
                      name="baseCharge"
                      placeholder="Base Charge"
                      value={formik.values.baseCharge}
                      onChange={formik.handleChange}
                      className="form-control"
                    />
                    <label>Base Charge *</label>
                  </div>

                  <div className="floating-label-group mb-3">
                    <input
                      type="number"
                      name="piecesPerKg"
                      placeholder="Pieces Per Kg"
                      value={formik.values.piecesPerKg}
                      onChange={formik.handleChange}
                      className="form-control"
                    />
                    <label>Pieces Per Kg *</label>
                  </div>

                  <div className="floating-label-group mb-3">
                    <input
                      type="number"
                      name="perKgCharge"
                      placeholder="Per Kg Charge"
                      value={formik.values.perKgCharge}
                      onChange={formik.handleChange}
                      className="form-control"
                    />
                    <label>Per Kg Charge *</label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    {editId ? "Update Rule" : "Add Rule"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingOffer;
